import React, { useState, useEffect, useContext }  from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import LayoutNavSectionCombinationList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationList.jsx';
import SelectedList from 'components/layout_and_quote_builder/quote_builder/SelectedList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import ChooseLayoutDimensions from 'components/layout_and_quote_builder/options/ChooseLayoutDimensions.jsx';
import GeneralNavSection from 'components/layout_and_quote_builder/quote_builder/GeneralNavSection.jsx';
import { shouldShowSectionLabel } from 'components/layout_and_quote_builder/utils/navSectionUtils.js';

const LayoutAndQuoteBuilderNavSection = ( props ) => {
  const myLayoutContext = useContext(LayoutContext);

  // const fallbackMaterialValues = () => {
  //   return {
  //     layout_dimension_ids: [21],
  //     layout_dimension_unit: myLayoutContext.layoutOptions.layout_dimension_unit,
  //     layout_dimension_top_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,

  //     layout_dimension_top_margin_printing: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_printing: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_printing: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,
  //   }
  // }

  // Use props if provided, otherwise use local state (for backwards compatibility)
  const selectedList = props.selectedList !== undefined ? props.selectedList : [];
  const setSelectedList = props.setSelectedList || (() => {});
  const previousSelectedList = props.previousSelectedList;

  const [materialLayoutDimensions, setMaterialLayoutDimensions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [manuallyEnabled, setManuallyEnabled] = useState(false);

  // Fetch default material values from API on mount
  useEffect(() => {
    const fetchDefaultMaterialValues = async () => {
      if(props.navSection.index == undefined) {
        return ;
      }
      try {
        const response = await fetch(
          `/manufacturing-cost-calculator/${window.user_quote_id}/default-material-values/${props.navSection.index}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch default material values');
        }
        const data = await response.json();
        setMaterialLayoutDimensions(data);
      } catch (error) {
        console.error('Error fetching default material values:', error);
        // Fallback to context values on error
        // setMaterialLayoutDimensions(fallbackMaterialValues());
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaultMaterialValues();
  }, []);

  // If this section's (inherited) solution is cleared, re-gate it so its enable button reappears,
  // rather than relying on the section being remounted. Harmless for non-linked sections, which do
  // not use manuallyEnabled.
  useEffect(() => {
    if (selectedList.length === 0) {
      setManuallyEnabled(false);
    }
  }, [selectedList.length]);

  const navSectionLabel = () => {
    if(props.navSection.label) {
      return <span className={styles.navSectionLabel}> {props.navSection.label} </span>
    }
  }

  const chooseLayoutDimensionsComponent = () => {
    return <ChooseLayoutDimensions
      defaultValues={materialLayoutDimensions}
      publicSetter={setMaterialLayoutDimensions}
      index={props.index}
    />
  }

  // Admin-only: append the section's part_type to its title (e.g. "Corrugated cardboard
  // (corrugated_cardboard_flexo)"), so an admin can see exactly which part_type a section resolved to
  // and flag anything wrong. Hidden from customers.
  const adminPartTypeSuffix = () => {
    if (!window.current_user_admin || !props.navSection.part_type) {
      return null;
    }

    return (
      <span style={{ fontSize: '14px', color: '#888', fontWeight: 'normal' }}>
        {' '}({props.navSection.part_type})
      </span>
    );
  }

  // Admin-only sanity check: the material sheet dimensions computed for this section are hidden from
  // customers, but shown to admins next to the section heading so they can eyeball the chosen sheets
  // and flag anything that looks off. Nothing renders for non-admins or when no sheets were computed.
  const adminChosenMaterialDimensionsLine = () => {
    const chosenSheets = materialLayoutDimensions?.layout_dimensions || [];
    if (!window.current_user_admin || chosenSheets.length === 0) {
      return null;
    }

    return (
      <center>
        <div
          style={{ marginTop: '-10px', marginBottom: '10px' }}
          title="Admin only — the material sheet dimensions computed for this section. Flag if anything looks off."
        >
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
            Chosen material dimensions:
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {chosenSheets.map((sheet, sheetIndex) => (
              <div
                key={sheetIndex}
                style={{
                  fontSize: '13px',
                  color: '#555',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  backgroundColor: '#f9f9f9',
                }}
              >
                {sheet.description}
              </div>
            ))}
          </div>
        </div>
      </center>
    );
  }

  if(props.navSection.part_type == "general") {
    return <GeneralNavSection totalNavSectionSize={props.totalNavSectionSize} />
  }

  // Show loading state while fetching default material values
  if (isLoading || !materialLayoutDimensions) {
    return (
      <div className={styles.LayoutAndQuoteBuilderNavSection}>
        <div className={styles.nav_section}>
          {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
            <center> <h2> { props.navSection.label} </h2> </center>
          )}
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {t('quote_builder.loading')}...
          </div>
        </div>
      </div>
    );
  }

  // A linked section (e.g. corrugated_cardboard_offset) inherits its solution from a parent section
  // (carton_lamination). It stays visible but gated behind its own "Start Working" button until the
  // parent's solution is inherited (selectedList populated), so the user enables it deliberately.
  const userQuoteNavSection = window.user_quote_nav_sections?.find(
    section => section.sort_order === props.navSection.index
  );
  const isLinkedSection = !!userQuoteNavSection?.solution_user_quote_nav_section_id;
  const parentSolutionReady = selectedList.length > 0;

  const sectionBody = () => (
    <>
      { selectedList.length == 0 && <LayoutNavSectionCombinationList
        navSection={props.navSection}
        index={props.index}
        count={props.count}
        setSelectedList={setSelectedList}
        materialLayoutDimensions={materialLayoutDimensions}
        previousSelectedList={previousSelectedList}
      /> }

      <SelectedList selectedList={selectedList} setSelectedList={setSelectedList} config={props.config} index={props.index} totalNavSectionSize={props.totalNavSectionSize}/>
    </>
  );

  const enableButton = () => (
    <div style={{ textAlign: 'center' }}>
      {!parentSolutionReady && <p>{ t('quote_builder.this_section_not_yet_active') }</p>}
      <button
        onClick={() => setManuallyEnabled(true)}
        disabled={!parentSolutionReady}
        className="btn simple-btn"
      >
        { t('quote_builder.start_working_on_this_section') }
      </button>
      <hr />
    </div>
  );

  return (
    <>
    <div className={styles.LayoutAndQuoteBuilderNavSection}>

      <div className={styles.nav_section}>

        {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
          <center> <h2> { props.navSection.label}{adminPartTypeSuffix()} </h2> </center>
        )}

        {adminChosenMaterialDimensionsLine()}

        <div className={styles.nav_section_layout_selector}> <span> {chooseLayoutDimensionsComponent()} </span> </div>

        { isLinkedSection && !(parentSolutionReady && manuallyEnabled) ? enableButton() : sectionBody() }

      </div>
    </div>
    <br />
    <br />
    </>
  )
}

export default LayoutAndQuoteBuilderNavSection;
